import { useEffect } from 'react'
import type { ReactElement } from 'react'
import {
  PiBankBold,
  PiChartLineBold,
  PiCreditCardBold,
  PiMoneyWavyBold,
  PiWalletBold,
} from 'react-icons/pi'

import Head from 'next/head'
import Link from 'next/link'

import { COMPARE_NAV_LINKS, COMPARISON_LINKS } from '@ui/constants'
import { HeroBasic } from '@ui/hero'
import { MarketingLayout } from '@ui/layout/MarketingLayout'

import { useEventTracking } from '@website/hooks/useEventTracking'

const ICONS = {
  'Bank Accounts': <PiBankBold className="text-blue-700" />,
  'Budgeting Apps': <PiWalletBold className="text-emerald-600" />,
  'Cash Advances': <PiMoneyWavyBold className="text-green-600" />,
  'Credit Builder Accounts': <PiCreditCardBold className="text-violet-600" />,
  'Investing Apps': <PiChartLineBold className="text-teal-600" />,
}

const Comparisons = () => {
  return (
    <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {COMPARISON_LINKS.map((link) => (
        <Link
          key={link.name}
          href={link.href}
          className="flex flex-col justify-between rounded-xl border border-gray-300 bg-white p-6 pb-8 shadow transition-transform duration-200 md:hover:scale-105"
        >
          <div className="flex items-center justify-start gap-2">
            <div>{ICONS[link.name as keyof typeof ICONS]}</div>
            <h2 className="text-lg font-semibold text-gray-900">
              {link.title}
            </h2>
          </div>
          <p className="mt-2 text-sm text-gray-600">{link.description}</p>
          <button className="bg-blueberry-50 border-blueberry-500/20 mt-6 inline-block whitespace-nowrap rounded-md border px-4 py-2 text-center text-sm font-bold leading-6 text-gray-800 shadow-sm">
            {link.button}
          </button>
        </Link>
      ))}
    </div>
  )
}

export default function Compare() {
  const { trackEvent } = useEventTracking()

  useEffect(() => {
    trackEvent('view_compare')
  }, [])

  return (
    <>
      <Head>
        <title>Compare Financial Services - ATM.com</title>
      </Head>
      <div className="relative bg-[url('/img/our-mission-bg.svg')] bg-cover bg-center pt-[120px]">
        <HeroBasic
          className="!bg-transparent"
          title={
            <span className="font-display tracking-wider text-[#0a2d4a]">
              COMPARE FINANCIAL SERVICES
            </span>
          }
        />
      </div>
      <div className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <p className="mb-8 text-center text-lg text-[#0a2d4a]">
            Explore and compare the best financial tools to help you save money,
            build credit, manage your budget, and invest wisely. Find the right
            solution tailored to your financial goals.
          </p>
          <Comparisons />
        </div>
      </div>
    </>
  )
}

Compare.getLayout = (page: ReactElement) => (
  <MarketingLayout
    chin="white"
    banner={false}
    navLinks={COMPARE_NAV_LINKS}
    showBackButton
    mainClassName="-mt-[120px]"
  >
    {page}
  </MarketingLayout>
)
